import { v4 as uuidv4 } from 'uuid'
import {
    CreateCartItemCommand2,
    useCreateCartItemsMutation,
    useDeleteCartItemsByIdMutation,
} from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { PromiseResponse } from 'helpers/types'
import { catchErrorByStatus, handleError } from 'helpers/errors'
import { useCart } from 'store/cart-slice/hooks'
import { useAppSelector } from 'store/hooks'
import { getDatesBetweenDates } from 'helpers/dates'
import {
    PRODUCT_ID_CITY_BREAK_TICKET_ADULT,
    PRODUCT_ID_CITY_BREAK_TICKET_TEENAGER,
} from 'constants/app'
import { Ticket } from 'store/api-slice/types'
import { CityBreakTicket } from 'components/common/CityBreak/types'
import { CITY_BREAK_TICKET_PRICE } from 'constants/prices'
import useCityBreak from './useCityBreak'

export const useCityCart = (): {
    cartItems: Ticket[]
    datesRange: string[]
    ticketsData: CityBreakTicket[]
    daysNumber: number
    totalPrice: number
    addItemsToCart: (shouldSkipRefetch?: boolean) => Promise<PromiseResponse>
    deleteItemsFromCart: (
        shouldSkipRefetch?: boolean
    ) => Promise<PromiseResponse>
    isSubmitLoading: boolean
    isSubmitSuccess: boolean
    isSubmitError: boolean
    isDeleteLoading: boolean
    isDeleteSuccess: boolean
    isDeleteError: boolean
} => {
    const { language } = useApp()
    const { refetchCart, cartCreatorType } = useCart()
    const { isCityBreakApplicableForAccommo } = useCityBreak()

    const { ticketsCityBreak: cartItems, accommodation } = useAppSelector(
        (state) => state.cart.items
    )
    const { accommodation: accommodationFromState } = useAppSelector(
        (state) => state
    )

    const [postCartItems, postStatus] = useCreateCartItemsMutation()
    const {
        isLoading: isSubmitLoading,
        isSuccess: isSubmitSuccess,
        isError: isSubmitError,
    } = postStatus

    const [deleteCartItems, deleteStatus] = useDeleteCartItemsByIdMutation()
    const {
        isLoading: isDeleteLoading,
        isSuccess: isDeleteSuccess,
        isError: isDeleteError,
    } = deleteStatus

    // TODO: move into helper/s
    const ticketsDates = cartItems.map(({ detail: { date } }) => date)
    const datesRange = ticketsDates
        .filter((date, index) => ticketsDates.indexOf(date) === index)
        .sort()

    const daysNumber = datesRange.length

    const totalPrice = cartItems
        .map(({ unitPrice }) => unitPrice || 0)
        .reduce((partialSum, price) => partialSum + price, 0)

    // TODO: move into helper/s
    const adultTickets = cartItems.filter(
        ({ productId }) => productId === PRODUCT_ID_CITY_BREAK_TICKET_ADULT
    )
    const adultTicketsData: CityBreakTicket = {
        quantity: adultTickets.length / daysNumber,
        zone: 'jamango',
        person: 'adult',
        ticketType: 'day',
        price: CITY_BREAK_TICKET_PRICE,
    }
    const teenageTickets = cartItems.filter(
        ({ productId }) => productId === PRODUCT_ID_CITY_BREAK_TICKET_TEENAGER
    )
    const teenageTicketsData: CityBreakTicket = {
        quantity: teenageTickets.length / daysNumber,
        zone: 'jamango',
        person: 'teen_reduced',
        ticketType: 'day',
        price: CITY_BREAK_TICKET_PRICE,
    }
    // adult tickets are obligatory, unlike tickets for teenagers
    const ticketsData =
        teenageTickets.length > 0
            ? [adultTicketsData, teenageTicketsData]
            : [adultTicketsData]

    // create cart data for promotional tickets based on accommodation already added to cart
    const createCartItemsData = () => {
        const {
            dateFrom: dateFromFromCart,
            dateTo: dateToFromCart,
            adultCount: adultCountFromCart,
            childCount: childCountFromCart,
        } = accommodation?.detail || {}

        const {
            dates: datesFromState,
            guestsToAssign: {
                adults: adultCountFromState,
                kids: childCountFromState,
            },
        } = accommodationFromState

        const dateFrom = isCityBreakApplicableForAccommo
            ? datesFromState[0]
            : dateFromFromCart
        const dateTo = isCityBreakApplicableForAccommo
            ? datesFromState[datesFromState.length - 1]
            : dateToFromCart
        const adultCount = isCityBreakApplicableForAccommo
            ? adultCountFromState
            : adultCountFromCart
        const childCount = isCityBreakApplicableForAccommo
            ? childCountFromState
            : childCountFromCart

        if (!dateFrom || !dateTo || !adultCount || childCount === undefined)
            return []

        // array of all dates between start and end dates of stay in suntago village
        const daysOfStay = getDatesBetweenDates(dateFrom, dateTo)
        // common box id for all promotional tickets
        const boxId = uuidv4()

        // cart data for tickets for all days...
        const cartItemsAdultsData: CreateCartItemCommand2[] = daysOfStay
            .map((day) => {
                // ...for all adults on each day
                const adultTickets: CreateCartItemCommand2[] = Array(adultCount)
                    .fill(PRODUCT_ID_CITY_BREAK_TICKET_ADULT)
                    .map((productId) => ({
                        cartItemId: uuidv4(),
                        productId,
                        productType: 'ticket',
                        quantity: 1,
                        detail: {
                            date: day,
                            zone: 'jamango',
                            person: 'adult',
                            ticketTime: 'day',
                            boxId,
                        },
                    }))

                return [...adultTickets]
            })
            .flat()

        const cartItemsTeenagersData: CreateCartItemCommand2[] = daysOfStay
            .map((day) => {
                // ...for all kids/teenagers on each day
                const teenageTickets: CreateCartItemCommand2[] = Array(
                    childCount
                )
                    .fill(PRODUCT_ID_CITY_BREAK_TICKET_TEENAGER)
                    .map((productId) => ({
                        cartItemId: uuidv4(),
                        productId,
                        productType: 'ticket',
                        quantity: 1,
                        detail: {
                            date: day,
                            zone: 'jamango',
                            person: 'teen_reduced',
                            ticketTime: 'day',
                            boxId,
                        },
                    }))

                return [...teenageTickets]
            })
            .flat()

        return [...cartItemsAdultsData, ...cartItemsTeenagersData]
    }

    // add to cart promotional tickets based on accommodation already added to cart
    const addItemsToCart = async (shouldSkipRefetch?: boolean) => {
        const cartItemsCommandData = createCartItemsData()
        if (cartItemsCommandData.length === 0) return { isSuccess: false }

        try {
            const response = await postCartItems({
                createCartItemsCommand: {
                    items: [...cartItemsCommandData],
                },
                cartCreatorType,
                _locale: language,
            })
            handleError(response)
            !shouldSkipRefetch && refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            // try to reckognize one of 'global' errors first
            catchErrorByStatus({ error: errors as MessageErrorModel })
            throw errors
        }
    }

    const deleteItemsFromCart = async (shouldSkipRefetch?: boolean) => {
        const cartItemsIds = cartItems.map(({ cartItemId }) => cartItemId || '')
        const filteredCartItemsIds = cartItemsIds.filter((id) => id !== '')

        if (!filteredCartItemsIds.length) return { isSuccess: true }

        try {
            handleError(
                await deleteCartItems({
                    _locale: language,
                    cartCreatorType,
                    deleteCartItemsByIdCommand: {
                        cartItemUuids: [...filteredCartItemsIds],
                    },
                })
            )
            !shouldSkipRefetch && refetchCart()
            return { isSuccess: true }
        } catch (errors: unknown) {
            // try to reckognize one of 'global' errors first
            catchErrorByStatus({ error: errors as MessageErrorModel })
            throw errors
        }
    }

    return {
        cartItems,
        datesRange,
        daysNumber,
        ticketsData,
        totalPrice,
        addItemsToCart,
        deleteItemsFromCart,
        isSubmitLoading,
        isSubmitSuccess,
        isSubmitError,
        isDeleteLoading,
        isDeleteSuccess,
        isDeleteError,
    }
}
